import React, { useEffect, useState } from 'react';
import { Loader2, Save, Info, Code2, Plus, Trash2, Power, PowerOff, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * Admin-only card in Settings for managing site-wide tracking scripts.
 *
 * Supports:
 *  - Named IDs for GA4, Meta Pixel, LinkedIn Insight, GTM (frontend auto-
 *    generates the correct snippet from the ID). Save button applies.
 *  - Multiple named free-form snippets targeted at either ``<head>`` or
 *    end-of-``<body>``. Each snippet is saved independently — after "Add",
 *    the paste field clears so the next platform's code can be pasted.
 *    Individual snippets can be toggled on/off or deleted.
 *  - Targeting: "all pages" vs "specific pages" (a list of path prefixes).
 *
 * Non-admins never see this card.
 */
export function TrackingScriptsCard() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // snippet id currently mutating
  const [expanded, setExpanded] = useState({}); // snippet id → bool (expand code preview)

  // Draft snippet — cleared after successful save so admin can paste the next one.
  const [draft, setDraft] = useState({ label: '', location: 'head', code: '' });

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    api.get('/site-config/tracking-scripts')
      .then((r) => setCfg(r.data))
      .catch(() => toast.error('Could not load tracking scripts'));
  }, [user]);

  if (!user || user.role !== 'admin') return null;
  if (!cfg) return null;

  const set = (patch) => setCfg((prev) => ({ ...prev, ...patch }));

  const notifyInjector = () => {
    // Signal the injector to reload immediately (no page refresh needed).
    window.dispatchEvent(new CustomEvent('blayzrr:tracking-scripts-updated'));
  };

  const save = async () => {
    setBusy(true);
    try {
      const pagesList = (cfg.pages_text ?? (cfg.pages || []).join('\n'))
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        ga4_measurement_id: cfg.ga4_measurement_id || '',
        meta_pixel_id: cfg.meta_pixel_id || '',
        linkedin_insight_partner_id: cfg.linkedin_insight_partner_id || '',
        gtm_container_id: cfg.gtm_container_id || '',
        // Legacy single-slot fields — kept for backward compatibility with
        // any docs saved before the multi-snippet migration. New content
        // should be added via the snippet form below.
        custom_head_html: cfg.custom_head_html || '',
        custom_body_end_html: cfg.custom_body_end_html || '',
        scope: cfg.scope || 'all',
        pages: pagesList,
      };
      const res = await api.put('/admin/site-config/tracking-scripts', payload);
      setCfg({ ...res.data, pages_text: (res.data.pages || []).join('\n') });
      notifyInjector();
      toast.success('Tracking config saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const addSnippet = async () => {
    const label = (draft.label || '').trim();
    const code = (draft.code || '').trim();
    if (!label) {
      toast.error('Give the snippet a label (e.g. "Hotjar", "Clarity").');
      return;
    }
    if (!code) {
      toast.error('Paste the tracking code before saving.');
      return;
    }
    setAddBusy(true);
    try {
      const res = await api.post('/admin/site-config/tracking-scripts/snippet', {
        label,
        location: draft.location === 'body_end' ? 'body_end' : 'head',
        code,
        enabled: true,
      });
      setCfg({ ...res.data, pages_text: (res.data.pages || []).join('\n') });
      // Reset the draft — the paste field is now empty, ready for the next
      // platform's snippet.
      setDraft({ label: '', location: draft.location, code: '' });
      notifyInjector();
      toast.success(`Saved: ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save snippet');
    } finally {
      setAddBusy(false);
    }
  };

  const toggleSnippet = async (sn) => {
    setRowBusy(sn.id);
    try {
      const res = await api.patch(
        `/admin/site-config/tracking-scripts/snippet/${sn.id}`,
        { enabled: !sn.enabled },
      );
      setCfg({ ...res.data, pages_text: (res.data.pages || []).join('\n') });
      notifyInjector();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setRowBusy(null);
    }
  };

  const deleteSnippet = async (sn) => {
    if (!window.confirm(`Delete snippet "${sn.label}"? It will stop firing on all pages.`)) return;
    setRowBusy(sn.id);
    try {
      const res = await api.delete(`/admin/site-config/tracking-scripts/snippet/${sn.id}`);
      setCfg({ ...res.data, pages_text: (res.data.pages || []).join('\n') });
      notifyInjector();
      toast.success('Snippet deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setRowBusy(null);
    }
  };

  const snippets = Array.isArray(cfg.custom_snippets) ? cfg.custom_snippets : [];

  return (
    <Card className="border-border" data-testid="tracking-scripts-card">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Code2 size={18} /> Tracking scripts
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Admin-only. Inject analytics + pixel snippets across the site.
            </p>
          </div>
          <Button size="sm" onClick={save} disabled={busy} data-testid="tracking-scripts-save">
            {busy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving</> : <><Save size={14} className="mr-1.5" /> Save IDs & scope</>}
          </Button>
        </div>

        {/* Named IDs — we auto-generate the correct snippet from these. */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" htmlFor="ga4">GA4 measurement ID</Label>
            <Input id="ga4" value={cfg.ga4_measurement_id || ''} onChange={(e) => set({ ga4_measurement_id: e.target.value })} placeholder="G-XXXXXXXX" data-testid="tracking-scripts-ga4" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" htmlFor="meta">Meta Pixel ID</Label>
            <Input id="meta" value={cfg.meta_pixel_id || ''} onChange={(e) => set({ meta_pixel_id: e.target.value })} placeholder="1234567890" data-testid="tracking-scripts-meta" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" htmlFor="li">LinkedIn Insight partner ID</Label>
            <Input id="li" value={cfg.linkedin_insight_partner_id || ''} onChange={(e) => set({ linkedin_insight_partner_id: e.target.value })} placeholder="1234567" data-testid="tracking-scripts-linkedin" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium" htmlFor="gtm">GTM container ID</Label>
            <Input id="gtm" value={cfg.gtm_container_id || ''} onChange={(e) => set({ gtm_container_id: e.target.value })} placeholder="GTM-XXXXXX" data-testid="tracking-scripts-gtm" />
          </div>
        </div>

        {/* Multi-snippet management — one form, saves independently.  */}
        <div className="border-t border-border pt-5 space-y-4">
          <div>
            <h3 className="text-sm font-semibold">Custom snippets</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Paste any tracking code (Hotjar, Clarity, Reddit Pixel, X Pixel, etc.).
              Each save creates a named snippet — the paste box then clears, ready for the next one.
            </p>
          </div>

          {/* Draft form */}
          <div className="rounded-lg border border-border bg-secondary/30 p-4 space-y-3" data-testid="tracking-scripts-snippet-form">
            <div className="grid sm:grid-cols-[1fr_180px] gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground" htmlFor="snippet-label">
                  Label
                </Label>
                <Input
                  id="snippet-label"
                  value={draft.label}
                  onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                  placeholder="e.g. Hotjar, Reddit Pixel, X Pixel"
                  data-testid="tracking-scripts-snippet-label"
                  disabled={addBusy}
                  maxLength={80}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  Location
                </Label>
                <Select
                  value={draft.location}
                  onValueChange={(v) => setDraft((d) => ({ ...d, location: v }))}
                  disabled={addBusy}
                >
                  <SelectTrigger data-testid="tracking-scripts-snippet-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="head">Header (&lt;head&gt;)</SelectItem>
                    <SelectItem value="body_end">Footer (end of &lt;body&gt;)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground" htmlFor="snippet-code">
                Code
              </Label>
              <Textarea
                id="snippet-code"
                rows={5}
                value={draft.code}
                onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
                className="font-mono text-xs"
                placeholder="Paste the full <script>…</script> block here."
                data-testid="tracking-scripts-snippet-code"
                disabled={addBusy}
              />
              <p className="text-[11px] text-muted-foreground">
                Max 20 KB per snippet. Include the full <code>&lt;script&gt;</code> tags — inline scripts execute on injection.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={addSnippet}
                disabled={addBusy || !draft.label.trim() || !draft.code.trim()}
                data-testid="tracking-scripts-snippet-add"
              >
                {addBusy ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving</> : <><Plus size={14} className="mr-1.5" /> Add snippet</>}
              </Button>
            </div>
          </div>

          {/* Saved snippet list */}
          <div>
            <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
              <span>Saved snippets</span>
              <span data-testid="tracking-scripts-snippet-count">{snippets.length} total</span>
            </div>
            {snippets.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                No snippets yet. Paste one above to get started.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="tracking-scripts-snippet-list">
                {snippets.map((sn) => {
                  const isBusy = rowBusy === sn.id;
                  const isOpen = !!expanded[sn.id];
                  const codePreview = (sn.code || '').replace(/\s+/g, ' ').trim().slice(0, 120);
                  return (
                    <li
                      key={sn.id}
                      className={`rounded-lg border ${sn.enabled ? 'border-border bg-card' : 'border-dashed border-border bg-secondary/20'} p-3`}
                      data-testid={`tracking-scripts-snippet-row-${sn.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setExpanded((s) => ({ ...s, [sn.id]: !isOpen }))}
                          className="text-muted-foreground hover:text-foreground p-0.5"
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                        >
                          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">{sn.label}</span>
                            <Badge
                              variant="outline"
                              className="text-[9px] uppercase tracking-wider font-mono"
                            >
                              {sn.location === 'body_end' ? 'Footer' : 'Header'}
                            </Badge>
                            {!sn.enabled && (
                              <Badge variant="secondary" className="text-[9px] uppercase tracking-wider">
                                Disabled
                              </Badge>
                            )}
                          </div>
                          {!isOpen && (
                            <p className="text-[11px] font-mono text-muted-foreground truncate mt-0.5">
                              {codePreview}
                              {sn.code && sn.code.length > 120 ? '…' : ''}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSnippet(sn)}
                          disabled={isBusy}
                          className="h-7 w-7 p-0"
                          data-testid={`tracking-scripts-snippet-toggle-${sn.id}`}
                          aria-label={sn.enabled ? 'Disable' : 'Enable'}
                          title={sn.enabled ? 'Disable' : 'Enable'}
                        >
                          {isBusy ? <Loader2 size={13} className="animate-spin" /> : (sn.enabled ? <Power size={13} /> : <PowerOff size={13} className="text-muted-foreground" />)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSnippet(sn)}
                          disabled={isBusy}
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          data-testid={`tracking-scripts-snippet-delete-${sn.id}`}
                          aria-label="Delete"
                          title="Delete"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                      {isOpen && (
                        <pre className="mt-3 max-h-[220px] overflow-auto rounded bg-secondary/40 border border-border p-2 text-[11px] font-mono whitespace-pre-wrap break-all">
                          {sn.code}
                        </pre>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Targeting */}
        <div className="space-y-3 border-t border-border pt-5">
          <div>
            <Label className="text-xs font-medium">Fire on</Label>
            <RadioGroup
              value={cfg.scope || 'all'}
              onValueChange={(v) => set({ scope: v })}
              className="mt-2 grid grid-cols-2 gap-2"
              data-testid="tracking-scripts-scope"
            >
              <label className="flex items-center gap-2 border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-secondary/50">
                <RadioGroupItem value="all" data-testid="tracking-scripts-scope-all" />
                <span className="text-sm">All pages</span>
              </label>
              <label className="flex items-center gap-2 border border-border rounded-md px-3 py-2 cursor-pointer hover:bg-secondary/50">
                <RadioGroupItem value="pages" data-testid="tracking-scripts-scope-pages" />
                <span className="text-sm">Specific pages</span>
              </label>
            </RadioGroup>
          </div>

          {cfg.scope === 'pages' && (
            <div className="space-y-1.5">
              <Label className="text-xs font-medium" htmlFor="pages-list">Paths (one per line)</Label>
              <Textarea
                id="pages-list"
                rows={4}
                className="font-mono text-xs"
                value={cfg.pages_text ?? (cfg.pages || []).join('\n')}
                onChange={(e) => set({ pages_text: e.target.value })}
                placeholder="/&#10;/pricing&#10;/dashboard"
                data-testid="tracking-scripts-pages-list"
              />
              <p className="text-[11px] text-muted-foreground">
                Match by exact path or path prefix. Example: <code>/pricing</code> fires on <code>/pricing</code> and <code>/pricing/anything</code>.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-lg bg-secondary/40 border border-border p-3 flex items-start gap-2">
          <Info size={14} className="text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Scripts fire immediately on page load — they are considered
            essential analytics. Optional retargeting cookies stay behind the
            cookie-consent banner; enterprise-grade tracking here is treated
            as legitimate interest (audience analytics, conversion) not
            marketing profiling.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export default TrackingScriptsCard;
